import { contactLink } from "../lib/api";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Minus,
  ShoppingBag,
  BadgeCheck,
  Truck,
  Lock,
  Zap,
  Send,
} from "lucide-react";
import { siteConfig as fallback } from "../mock";
import { useCart } from "../context/CartContext";
import { useReveal } from "../hooks/useReveal";
import { useSite } from "../context/SiteContext";
import ImageGallery from "../components/ImageGallery";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, setIsOpen } = useCart();
  const { products, settings } = useSite();
  const siteConfig = settings || fallback;
  const product = products.find((p) => p.id === id);

  const [option, setOption] = useState(product?.options[0]);
  const [qty, setQty] = useState(1);

  useReveal([id]);
  useEffect(() => {
    window.scrollTo(0, 0);
    setOption(product?.options[0]);
    setQty(1);
  }, [id, product]);

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h2 className="text-2xl font-bold text-white mb-4">Product not found</h2>
        <Link to="/" className="text-gold hover:underline">
          Back to home
        </Link>
      </div>
    );
  }

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) addItem(product, option);
  };

  const related = products.filter((p) => p.id !== product.id).slice(0, 3);

  const perks = [
    { icon: Zap, text: "Delivered instantly" },
    { icon: Truck, text: "Tracking provided" },
    { icon: Lock, text: "100% discreet packaging" },
    { icon: BadgeCheck, text: "Premium 1:1 quality" },
  ];

  return (
    <div className="bg-navy min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-slate-300 hover:text-gold transition-colors mb-8"
        >
          <ArrowLeft size={18} /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="reveal">
            <ImageGallery
              images={product.images || [product.image]}
              alt={product.name}
            />
          </div>

          <div className="reveal">
            <p className="text-gold text-sm font-semibold uppercase tracking-widest mb-2">
              {product.category}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              {product.name}
            </h1>
            <p className="mt-3 text-gold font-display font-bold text-3xl">
              ${option?.price.toFixed(2)}
            </p>
            <p className="mt-5 text-slate-300 leading-relaxed">
              {product.description}
            </p>

            {product.options.length > 1 && (
              <div className="mt-7">
                <p className="text-white font-semibold mb-3">Choose a pack</p>
                <div className="flex flex-wrap gap-3">
                  {product.options.map((o) => (
                    <button
                      key={o.label}
                      onClick={() => setOption(o)}
                      className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                        option?.label === o.label
                          ? "bg-gold text-navy border-gold"
                          : "text-slate-200 border-white/15 hover:border-gold/50"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-7 flex items-center gap-4">
              <div className="flex items-center border border-gold/30 rounded-xl">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="h-12 w-12 flex items-center justify-center text-white hover:text-gold"
                >
                  <Minus size={16} />
                </button>
                <span className="w-10 text-center text-white font-semibold">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="h-12 w-12 flex items-center justify-center text-white hover:text-gold"
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                onClick={handleAdd}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold py-3.5 rounded-xl transition-colors duration-200"
              >
                <ShoppingBag size={18} /> Add to Cart
              </button>
            </div>

            <a
              {...contactLink(siteConfig.contact?.telegramLink)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-gold/30 hover:border-gold text-white py-3.5 rounded-xl transition-colors duration-200"
            >
              <Send size={17} /> Ask a Question
            </a>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {perks.map((p) => (
                <div key={p.text} className="flex items-center gap-2.5 text-slate-300 text-sm">
                  <span className="h-9 w-9 rounded-lg bg-navy-700 gold-border flex items-center justify-center text-gold">
                    <p.icon size={16} />
                  </span>
                  {p.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Related */}
        <div className="mt-20">
          <h3 className="text-2xl font-bold text-white mb-8">You May Also Like</h3>
          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                className="card-hover bg-navy-800 rounded-2xl overflow-hidden border border-white/10 block"
              >
                <div className="img-zoom h-48">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h4 className="text-white font-semibold">{p.name}</h4>
                  <p className="text-gold font-display font-bold mt-1">{p.priceLabel}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
